import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/cn';
import { canWrite, mutate } from '../lib/mutate';
import { formatCurrency, formatPlainDate } from '../lib/format';
import { useDebounced } from '../lib/useDebounced';
import { EmptyState, ErrorNote, LoadMore, Panel, SearchBox, Spinner } from '../components/ui';
import {
  PromotionScheduleDialog,
  type SchedulableGroup,
} from '../components/PromotionScheduleDialog';

interface Promotion {
  promotionId: number;
  barcode: string;
  productName: string | null;
  /** 'supplier' means the barcode is not in this shop's own catalogue. */
  nameSource: 'catalogue' | 'supplier' | null;
  groupKey: string;
  /** Rows in the whole deal, counted server-side so paging cannot skew it. */
  groupSize: number;
  deal: string;
  requiredQty: number;
  offerPrice: string;
  startDate: string;
  endDate: string;
  supplier: string | null;
  isActive: boolean;
  state: 'live' | 'scheduled' | 'expired' | 'inactive';
}

const STATES = [
  { key: '', label: 'All' },
  { key: 'live', label: 'Live' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'expired', label: 'Expired' },
] as const;

/** State is a status, so it ships with a written label rather than colour alone. */
const STATE_STYLES: Record<Promotion['state'], string> = {
  live: 'border-up/30 bg-up/5 text-up',
  scheduled: 'border-accent/30 bg-accent-soft text-accent',
  expired: 'border-rule bg-sunken text-ink-faint',
  inactive: 'border-warn/30 bg-warn-soft text-warn',
};

interface Group {
  key: string;
  rows: Promotion[];
  /** Group-level facts, taken from the first row — they are equal across it. */
  head: Promotion;
}

/**
 * Collects consecutive rows into their deals.
 *
 * Consecutive, not keyed into a map: the service orders by
 * `(enddate, groupkey, promotionid)` precisely so a group's rows arrive in one
 * run, and walking the list preserves that order. Bucketing by key would work
 * too, but it would silently paper over an ordering regression by re-joining
 * rows the server had scattered — and then a deal split across a page boundary
 * would look fine here while being wrong.
 */
function intoGroups(promotions: Promotion[]): Group[] {
  const groups: Group[] = [];

  for (const promotion of promotions) {
    const last = groups[groups.length - 1];
    if (last && last.key === promotion.groupKey) {
      last.rows.push(promotion);
    } else {
      groups.push({ key: promotion.groupKey, rows: [promotion], head: promotion });
    }
  }

  return groups;
}

export function Promotions(): React.JSX.Element {
  const { activeStore } = useAuth();
  const storeId = activeStore?.storeId;
  const [state, setState] = useState('');
  const [search, setSearch] = useState('');
  const [scheduling, setScheduling] = useState<SchedulableGroup | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const applied = useDebounced(search);
  const queryClient = useQueryClient();
  const editable = canWrite(activeStore?.role);

  const { data, error, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['promotions', storeId, state, applied],
      queryFn: ({ pageParam }) =>
        api.get<{ promotions: Promotion[]; nextCursor: string | null }>(
          `/stores/${storeId}/promotions?limit=50${state ? `&state=${state}` : ''}` +
            `${applied ? `&search=${encodeURIComponent(applied)}` : ''}` +
            `${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
        ),
      initialPageParam: '',
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      enabled: Boolean(storeId),
    });

  const promotions = data?.pages.flatMap((page) => page.promotions) ?? [];
  const groups = intoGroups(promotions);

  // Activation and dates belong to the whole deal, never to one barcode, which
  // is why both controls live on the group header rather than on every row.
  const toggle = useMutation({
    mutationFn: ({ groupKey, isActive }: { groupKey: string; isActive: boolean }) =>
      mutate.patch(`/stores/${storeId}/promotions/groups/${groupKey}`, { isActive }),
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['promotions', storeId] });
    },
    onError: (caught: unknown) =>
      setActionError(caught instanceof Error ? caught.message : 'Could not change that.'),
  });

  return (
    <>
      <header className="mb-4">
        <p className="qs-eyebrow">Promotions</p>
        <h1 className="text-lg font-medium tracking-tight">Deals</h1>
      </header>

      {/* Chips and search on one row beneath the heading, as on Sales and
          Purchases — the box needs the width to show its own placeholder. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex gap-1" role="group" aria-label="Filter by state">
          {STATES.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setState(option.key)}
              aria-pressed={state === option.key}
              className={cn(
                'rounded border px-2 py-1 text-xs',
                state === option.key
                  ? 'border-accent bg-accent-soft font-medium text-accent'
                  : 'border-rule text-ink-muted hover:border-rule-strong hover:text-ink',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        <SearchBox
          value={search}
          onChange={setSearch}
          label="Find a deal by product name or barcode"
          className="max-w-72 flex-1"
        />
      </div>

      {error && <ErrorNote error={error} />}
      {actionError && <ErrorNote error={new Error(actionError)} />}

      <Panel>
        {isPending ? (
          <Spinner label="Loading promotions…" />
        ) : groups.length === 0 ? (
          <EmptyState>
            {applied
              ? `No deal has a product matching "${applied}".`
              : 'No promotions match that filter.'}
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <caption className="sr-only">
                Promotions, grouped by deal. Each deal is introduced by a heading row carrying
                its supplier, dates and state.
              </caption>
              <thead>
                <tr className="border-b border-rule">
                  <th className="qs-eyebrow py-2 pl-3 text-left">Product</th>
                  <th className="qs-eyebrow py-2 text-left">Deal</th>
                  <th className="qs-eyebrow hidden py-2 text-right md:table-cell">Qty</th>
                  <th className="qs-eyebrow py-2 pr-3 text-right">Offer</th>
                </tr>
              </thead>

              {/*
                A tbody per deal rather than one tbody with heading rows in it.
                It is the semantically correct grouping, it lets the whole block
                take a border without every row carrying one, and it keeps all
                deals on a single column grid — a table per deal would let each
                one size its columns differently, which is precisely the
                alignment a console trades density for.
              */}
              {groups.map((group) => (
                <tbody key={group.key} className="border-t-2 border-rule-strong first:border-t-0">
                  <tr className="bg-sunken">
                    <th colSpan={4} scope="colgroup" className="px-3 py-1.5 text-left">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {/* A deal with no supplier was keyed in at the till
                            rather than loaded from a wholesaler's file — the
                            till marks those with a MANUAL group key, so the
                            page uses the shop's own word for them. */}
                        <span className="text-xs font-medium">
                          {group.head.supplier ?? 'MANUAL'}
                        </span>
                        <span className="qs-figure text-2xs text-ink-muted">
                          {formatPlainDate(group.head.startDate)} –{' '}
                          {formatPlainDate(group.head.endDate)}
                        </span>
                        <span
                          className={cn(
                            'rounded border px-1.5 py-0.5 text-2xs capitalize',
                            STATE_STYLES[group.head.state],
                          )}
                        >
                          {group.head.state}
                        </span>
                        {/* The count and the controls travel together, on their
                            own line on a phone.

                            `basis-full` below `sm`: the supplier, dates and
                            state chip fill the first line and wrap whatever is
                            left, so the count landed beside the chip on one deal
                            and above the buttons on the next — the same header
                            laid out two different ways down a single list.
                            Keeping the pair on one line makes the count read as
                            what the buttons will move, which is what it is
                            for. */}
                        <span className="flex basis-full items-center gap-x-2 sm:ml-auto sm:basis-auto">
                          {/* `groupSize` is the whole deal; `rows.length` is what
                              is on screen, and they differ while a deal
                              straddles a page boundary. */}
                          <span className="text-2xs text-ink-faint">
                            {group.head.groupSize === group.rows.length
                              ? `${group.head.groupSize} ${group.head.groupSize === 1 ? 'line' : 'lines'}`
                              : `${group.rows.length} of ${group.head.groupSize} lines`}
                          </span>

                        {editable && (
                          <span className="ml-auto flex gap-1">
                            <button
                              type="button"
                              disabled={toggle.isPending || group.head.state === 'expired'}
                              title={
                                group.head.state === 'expired'
                                  ? 'An expired promotion cannot be activated. Move its dates first.'
                                  : `Affects all ${group.head.groupSize} lines in this deal`
                              }
                              onClick={() =>
                                toggle.mutate({
                                  groupKey: group.key,
                                  isActive: !group.head.isActive,
                                })
                              }
                              className="rounded border border-rule bg-surface px-2 py-0.5 text-2xs text-ink-muted hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              {group.head.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setScheduling({
                                  groupKey: group.key,
                                  startDate: group.head.startDate,
                                  endDate: group.head.endDate,
                                  deal: group.head.deal,
                                  memberCount: group.head.groupSize,
                                })
                              }
                              className="rounded border border-rule bg-surface px-2 py-0.5 text-2xs text-ink-muted hover:border-accent hover:text-accent"
                            >
                              Dates
                            </button>
                          </span>
                        )}
                        </span>
                      </div>
                    </th>
                  </tr>

                  {group.rows.map((promotion) => (
                    <tr key={promotion.promotionId} className="border-b border-rule last:border-0">
                      <td className="py-1.5 pl-3">
                        {/* Narrow on phones, as on the catalogue and for the
                            same reason: 20rem of product name does not fit
                            beside the deal and its offer price in a 390px
                            column, and the two things this table exists to show
                            were pushed off the right edge behind a swipe. */}
                        <p
                          className="max-w-[9.5rem] truncate text-xs md:max-w-xs"
                          title={promotion.productName ?? ''}
                        >
                          {promotion.productName ?? (
                            <span className="text-ink-faint">Not in any catalogue</span>
                          )}
                        </p>
                        <p className="text-2xs text-ink-faint">
                          <span className="qs-figure">{promotion.barcode}</span>
                          {/* Names the source rather than flagging a fault, and
                              is styled to match: this barcode is not in the
                              shop's own catalogue, so the name shown is the
                              wholesaler's description of it. Muted rather than
                              the warning colour — it is provenance, and an
                              amber label here read as "something is wrong with
                              this line" when nothing is. */}
                          {promotion.nameSource === 'supplier' && (
                            <span className="ml-1.5 text-ink-muted">Supplier Catalogue</span>
                          )}
                        </p>
                      </td>
                      <td className="max-w-40 truncate py-1.5 text-xs" title={promotion.deal}>
                        {promotion.deal}
                      </td>
                      <td className="qs-figure hidden py-1.5 text-right text-xs md:table-cell">
                        {promotion.requiredQty}
                      </td>
                      <td className="qs-figure py-1.5 pr-3 text-right text-xs">
                        {formatCurrency(promotion.offerPrice)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}

        <LoadMore
          shown={promotions.length}
          hasMore={Boolean(hasNextPage)}
          isLoading={isFetchingNextPage}
          onLoad={() => void fetchNextPage()}
          noun="lines shown"
        />
      </Panel>

      {/* Mounted only while a group is being rescheduled, so each open starts
          without the previous attempt's rejection on screen. */}
      {storeId && scheduling && (
        <PromotionScheduleDialog
          storeId={storeId}
          group={scheduling}
          onClose={() => setScheduling(null)}
        />
      )}
    </>
  );
}
